import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Paper, Grid, Button, 
    Alert, Snackbar, Skeleton 
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import { SettingsService } from '../../api/services/settingsService';
import EmailTemplateFormModal from '../../components/EmailTemplateFormModal';
import SendTestEmailModal from '../../components/SendTestEmailModal';

const formatTemplateName = (name) => {
    if (!name) return '';
    if (name.includes('_') || name === name.toUpperCase()) {
        return name
            .split('_')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(' ');
    }
    return name;
};

export default function EmailTemplatesPage() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    // Modal States
    const [modalOpen, setModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [testModalOpen, setTestModalOpen] = useState(false);
    const [templateToTest, setTemplateToTest] = useState(null);

    useEffect(() => {
        fetchEmailTemplates();
    }, []);

    const fetchEmailTemplates = async () => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const data = await SettingsService.getEmailTemplates(tenantId);
            setTemplates(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to fetch email templates:', error);
            setNotification({ open: true, message: 'Failed to load email templates.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (template) => {
        setEditingTemplate(template);
        setModalOpen(true);
    };

    const handleSaveTemplate = async (templateData) => {
        setSaving(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            await SettingsService.updateEmailTemplate(editingTemplate.id, templateData, tenantId);
            setNotification({ open: true, message: 'Email template updated successfully!', severity: 'success' });
            setModalOpen(false);
            fetchEmailTemplates();
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to update email template', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleOpenTestModal = (template) => {
        setTemplateToTest(template);
        setTestModalOpen(true);
    };

    const handleSendTestEmail = async (recipientEmail) => {
        setTestLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            await SettingsService.sendTestEmail(templateToTest.id, recipientEmail, tenantId);
            setNotification({ open: true, message: 'Test email sent successfully!', severity: 'success' });
            setTestModalOpen(false);
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to send test email', severity: 'error' });
        } finally {
            setTestLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                        Email Templates
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        Manage automated transactional guest correspondence, triggers, and notification styling.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Button 
                        variant="outlined" 
                        startIcon={<RefreshIcon sx={{ fontSize: 18 }} />} 
                        onClick={fetchEmailTemplates}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Template Cards Grid */}
            {loading && templates.length === 0 ? (
                <Grid container spacing={2.5}>
                    {Array.from(new Array(6)).map((_, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={`skeleton-${idx}`}>
                            <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Skeleton variant="text" width="50%" height={28} />
                                    <Skeleton variant="rectangular" width={65} height={24} sx={{ borderRadius: '16px' }} />
                                </Box>
                                <Skeleton variant="text" width="80%" height={20} sx={{ mb: 3 }} />
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Skeleton variant="rectangular" width={110} height={32} sx={{ borderRadius: '8px' }} />
                                    <Skeleton variant="rectangular" width={90} height={32} sx={{ borderRadius: '8px' }} />
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            ) : templates.length > 0 ? (
                <Grid container spacing={2.5}>
                    {templates.map((template, idx) => {
                        const isActive = Boolean(template.enabled ?? template.isActive);
                        return (
                            <Grid item xs={12} sm={6} md={4} key={template.id || idx}>
                                <Paper sx={{ 
                                    p: 2.5, 
                                    border: '1px solid #e5e7eb', 
                                    borderRadius: '12px', 
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    bgcolor: '#ffffff',
                                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)',
                                    transition: 'all 0.2s ease-in-out', 
                                    '&:hover': { 
                                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                                        borderColor: '#cbd5e1'
                                    } 
                                }}>
                                    {/* Card Header: Title & Status */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.975rem', color: '#111827' }}>
                                            {formatTemplateName(template.name)}
                                        </Typography>
                                        <Box 
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: isActive ? 0.75 : 0.5,
                                                px: 1.25,
                                                py: 0.35,
                                                borderRadius: '16px',
                                                bgcolor: isActive ? '#ecfdf5' : '#f3f4f6',
                                                color: isActive ? '#027a48' : '#4b5563',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                border: `1px solid ${isActive ? '#a6f4c5' : '#e5e7eb'}`,
                                                userSelect: 'none'
                                            }}
                                        >
                                            {isActive ? (
                                                <>
                                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#12b76a' }} />
                                                    Active
                                                </>
                                            ) : (
                                                <>
                                                    Inactive
                                                    <EditIcon sx={{ fontSize: 13, color: '#9ca3af' }} />
                                                </>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Card Subtitle */}
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: '#6b7280', 
                                            fontSize: '0.825rem',
                                            mb: 3, 
                                            minHeight: 22,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {template.subject || `${formatTemplateName(template.name)} -`}
                                    </Typography>
                                    
                                    {/* Card Footer: Action Buttons */}
                                    <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Button 
                                            variant="outlined" 
                                            color="primary"
                                            size="small" 
                                            startIcon={<EditIcon sx={{ fontSize: '15px !important' }} />}
                                            onClick={() => handleOpenModal(template)}
                                            sx={{
                                                borderRadius: '8px',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '0.8rem',
                                                px: 1.5,
                                                py: 0.5,
                                            }}
                                        >
                                            Edit Content
                                        </Button>
                                        <Button 
                                            variant="text" 
                                            size="small" 
                                            startIcon={<SendIcon sx={{ fontSize: '15px !important' }} />}
                                            onClick={() => handleOpenTestModal(template)}
                                            sx={{
                                                borderRadius: '8px',
                                                color: '#4b5563',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: '0.8rem',
                                                px: 1.5,
                                                py: 0.5,
                                                '&:hover': {
                                                    bgcolor: '#f3f4f6',
                                                    color: '#111827'
                                                }
                                            }}
                                        >
                                            Send Test
                                        </Button>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            ) : (
                <Paper sx={{ p: 6, textAlign: 'center', border: '1px dashed #d1d5db', borderRadius: '12px', bgcolor: '#ffffff' }}>
                    <Typography color="text.secondary">No email templates found for this property.</Typography>
                </Paper>
            )}

            {/* Modals */}
            <EmailTemplateFormModal 
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSaveTemplate}
                editingTemplate={editingTemplate}
                loading={saving}
            />

            <SendTestEmailModal 
                open={testModalOpen}
                onClose={() => setTestModalOpen(false)}
                onSend={handleSendTestEmail}
                templateName={templateToTest?.name}
                loading={testLoading}
            />

            {/* Notification Toast */}
            <Snackbar 
                open={notification.open} 
                autoHideDuration={6000} 
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={notification.severity} sx={{ width: '100%', borderRadius: 2 }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
